/* eslint-disable no-undef */
import axios from 'axios';
import Hex from 'crypto-js/enc-hex';
import HmacSHA256 from 'crypto-js/hmac-sha256';
import { Dispatch } from 'redux';
import config from '../../../../app.config.json';
import { setLoggedIn } from '../reducers/user';
import { API_URL } from '../services/axios';
import logging from '../services/logging';
import * as storage from '../services/storage';
import { setItem, getItem, removeItem } from '../services/storage.secure';
import { NetworkContext } from '../types/network';
import isReactNative from './isReactNative';
import parseJwt from './jwt';
import setEmailHashes from './liveramp';

export const DEVICE_ID = 'deviceIdv4';
export const CLIENT_TOKEN = 'client-token';
export const EMAIL_HASHES = 'email-hashes';
export const PROFILE = 'profile_unsafe';
const CLIENT_SECRET = isReactNative() ? 'WAKXQjmMzcCkv4cfNttjmd24' : 'jG2jS9iU8cD1wX6iR1cM2bH9c';
const getServerToken = () => (isReactNative() ? config.serverToken : process?.env?.SERVER_TOKEN);

declare global {
  interface Window {
    ats: any;
  }
}

export interface TokenParams {
  email?: string;
  password?: string;
  aAccessToken?: string;
  aBundleId?: string;
  fbUserId?: string;
  fbAccessToken?: string;
  gAccessToken?: string;
  logout?: boolean;
  inviteId?: number;
  inviteToken?: string;
}

export interface TokenResponse {
  token: {
    authToken: string | null;
    authTokenExpires: string;
    loggedIn: boolean;
    currency: string;
  };
  error?: string;
}

export const getLoginHeader = async (ctx?: NetworkContext) => {
  const clientToken = await getItem(CLIENT_TOKEN, ctx);
  const headers: { [key: string]: string } = {};
  const serverToken = getServerToken();

  if (serverToken) {
    headers['X-Server-Token'] = serverToken;
  }

  if (clientToken) {
    headers['X-Client-Token'] = clientToken;
  }

  return headers;
};

export const setClientToken = async (
  clientToken: string,
  emailHashes?: string[],
  dispatch?: Dispatch,
  ctx?: NetworkContext,
) => {
  await setItem(CLIENT_TOKEN, clientToken, ctx);

  const jwt = parseJwt(clientToken);

  if (dispatch) {
    dispatch(setLoggedIn(!!jwt?.u));
  }

  if (emailHashes) {
    await storage.setItem(EMAIL_HASHES, emailHashes, ctx);
  }
};

export const getClientToken = async (ctx?: NetworkContext) => {
  const headers = await getLoginHeader(ctx);

  try {
    const result = await axios({
      method: 'GET',
      url: `${API_URL}/login`,
      headers,
      withCredentials: true,
    });

    await setClientToken(result.headers['x-client-token'], result.data, undefined, ctx);

    return result.data.loggedIn;
  } catch {
    return false;
  }
};

export const clearClientToken = async (ctx?: NetworkContext) => {
  await removeItem(CLIENT_TOKEN, ctx);
  await storage.removeItem(EMAIL_HASHES, ctx);
  const headers = await getLoginHeader(ctx);

  const result = await axios({
    method: 'POST',
    url: `${API_URL}/logout`,
    headers,
    withCredentials: true,
  });

  await setClientToken(result.headers['x-client-token'], result.data.emailHashes, undefined, ctx);
};

export const updateClientToken = async (tokenParams?: TokenParams, ctx?: NetworkContext): Promise<TokenResponse> => {
  try {
    const headers = await getLoginHeader(ctx);

    const result = await axios({
      method: 'POST',
      url: `${API_URL}/login`,
      headers,
      data: { ...tokenParams },
      withCredentials: true,
    });

    await setClientToken(result.headers['x-client-token'], result.data.emailHashes, undefined, ctx);
    setEmailHashes(result.data.emailHashes);

    logging.logCrumb({
      message: 'New auth token',
      metaData: {
        expires: result.data.expires,
        loggedIn: result.data.loggedIn,
      },
    });

    return { token: result.data };
  } catch (error: any) {
    if (error && error.response && error.response.status !== 400) {
      logging.logError(error);
    }

    let errorStatus = 'LOGIN_ERROR';
    if (error.response?.data?.invalidCredentials) errorStatus = 'INVALID_CREDENTIALS';
    if (error.response?.data?.emailRequired) errorStatus = 'MISSING_EMAIL';
    if (error.response?.data?.passwordRequired) errorStatus = 'MISSING_PASSWORD';

    return {
      token: {
        authToken: null,
        authTokenExpires: '',
        loggedIn: false,
        currency: 'GBP',
      },
      error: errorStatus,
    };
  }
};

export const getAuthHeaders = async (requestUrl: string, ctx?: NetworkContext) => {
  const headers: { [key: string]: string } = {};
  const clientToken = await getItem(CLIENT_TOKEN, ctx);
  const deviceId = await getItem(DEVICE_ID, ctx);
  const url = requestUrl.replace(/http:\/\/localhost:\d+\/_wapi/g, config.apiUrl);
  const signatureBytes = HmacSHA256(url, CLIENT_SECRET);
  const serverToken = getServerToken();

  headers['X-API-Signature'] = Hex.stringify(signatureBytes);

  if (deviceId) {
    headers['X-API-Device'] = deviceId;
    await removeItem(DEVICE_ID, ctx);
  }

  // If the user has no client token, use server token instead. The endpoint used will return a client token
  // This is not used on web because the front-end cannot read the clientToken cookie
  if (!clientToken && serverToken && (!!ctx || isReactNative())) {
    headers['X-Server-Token'] = serverToken;
  }

  // For server request, cookie must be manually set
  if (clientToken && !!ctx) {
    headers.Cookie = `client-token=${clientToken}`;
  }

  // For React Native, Server Token must always be passed with client token
  if (clientToken && isReactNative() && serverToken) {
    headers['X-Client-Token'] = clientToken;
    headers['X-Server-Token'] = serverToken;
  }

  return headers;
};
